/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Intent:     owner assigns which services this agenda offers — operational, scannable
 * Hierarchy:  assigned list leads; Agregar opens searchable sheet; Guardar stays filled
 * Palette:    agenda tokens — outline secondary, no mint wash
 * Depth:      borders + hairlines only
 */
import React, { useMemo, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { MasterFilterSheetModal } from '../../shared/filters/MasterFilterSheetModal';
import { agenda, agendaFonts } from '../domain/agendaTheme';
import type { ServiceProductOption } from '../domain/types';
import { formatMoney } from '../../shared/format/public-api';

type Props = {
  allServices: ServiceProductOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  onSave: () => void;
  onCancel?: () => void;
  saving?: boolean;
  /** Hide the save/cancel actions when the parent provides its own footer. */
  hideSaveAction?: boolean;
  /** Hide the top hint when the parent already provides a section title. */
  hideHint?: boolean;
  /** Panel-aware wide layout from parent. */
  isWide?: boolean;
};

export function ResourceServicesEditor({
  allServices,
  selectedIds,
  onChange,
  onSave,
  onCancel,
  saving = false,
  hideSaveAction = false,
  hideHint = false,
  isWide = false,
}: Props) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const assigned = useMemo(
    () => allServices.filter((s) => selectedSet.has(s.id)),
    [allServices, selectedSet],
  );
  const availableOptions = useMemo(
    () =>
      allServices
        .filter((s) => !selectedSet.has(s.id))
        .map((s) => ({
          id: s.id,
          name: s.name,
          subtitle: `${s.durationMinutes} min · ${formatMoney(s.price)}`,
        })),
    [allServices, selectedSet],
  );

  const isAll = selectedIds.length === 0;

  const removeId = (id: string) => {
    onChange(selectedIds.filter((x) => x !== id));
  };

  const addId = (id: string | null) => {
    if (!id || selectedSet.has(id)) return;
    onChange([...selectedIds, id]);
  };

  return (
    <View style={styles.wrap}>
      {!hideHint ? (
        <Text style={styles.hint}>
          Servicios que ofrece. Si no asignás ninguno, atiende todos.
        </Text>
      ) : null}

      {allServices.length === 0 ? (
        <Text style={styles.emptyCatalog}>No hay servicios en el catálogo.</Text>
      ) : isAll ? (
        <View style={styles.emptyAll}>
          <Text style={styles.emptyAllTitle}>Atiende todos los servicios</Text>
        </View>
      ) : (
        <View style={styles.list}>
          {assigned.map((s, index) => (
            <View
              key={s.id}
              style={[styles.row, index < assigned.length - 1 && styles.rowDivider]}
            >
              <View style={styles.rowBody}>
                <Text style={styles.rowName} numberOfLines={1}>
                  {s.name}
                </Text>
                <Text style={styles.rowMeta}>
                  {s.durationMinutes} min · {formatMoney(s.price)}
                </Text>
              </View>
              <TouchableOpacity
                onPress={() => removeId(s.id)}
                hitSlop={8}
                disabled={saving}
                accessibilityRole="button"
                accessibilityLabel={`Quitar ${s.name}`}
              >
                <Text style={styles.removeText}>Quitar</Text>
              </TouchableOpacity>
            </View>
          ))}
        </View>
      )}

      <View style={[styles.actions, isWide && styles.actionsWide]}>
        {!isAll ? (
          <TouchableOpacity
            style={[styles.outlineBtn, saving && styles.disabled]}
            onPress={() => onChange([])}
            disabled={saving}
            accessibilityRole="button"
          >
            <Text style={styles.outlineBtnText}>Atiende todos</Text>
          </TouchableOpacity>
        ) : null}
        <TouchableOpacity
          style={[
            styles.outlineBtn,
            (saving || availableOptions.length === 0) && styles.disabled,
          ]}
          onPress={() => setPickerOpen(true)}
          disabled={saving || availableOptions.length === 0 || allServices.length === 0}
          accessibilityRole="button"
        >
          <Text style={styles.outlineBtnText}>
            {availableOptions.length === 0 && allServices.length > 0
              ? 'Todos asignados'
              : 'Agregar servicio'}
          </Text>
        </TouchableOpacity>
        {isWide && !hideSaveAction ? (
          <>
            {onCancel ? (
              <TouchableOpacity
                style={[styles.outlineBtn, saving && styles.disabled]}
                onPress={onCancel}
                disabled={saving}
                accessibilityRole="button"
              >
                <Text style={styles.outlineBtnText}>Cancelar</Text>
              </TouchableOpacity>
            ) : null}
            <TouchableOpacity
              style={[styles.primaryBtnInline, saving && styles.disabled]}
              onPress={onSave}
              disabled={saving}
              accessibilityRole="button"
            >
              <Text style={styles.primaryBtnText}>
                {saving ? 'Guardando…' : 'Guardar servicios'}
              </Text>
            </TouchableOpacity>
          </>
        ) : null}
      </View>

      {!isWide && !hideSaveAction ? (
        <View style={styles.saveRow}>
          {onCancel ? (
            <TouchableOpacity
              style={[styles.outlineBtnFlex, saving && styles.disabled]}
              onPress={onCancel}
              disabled={saving}
              accessibilityRole="button"
            >
              <Text style={styles.outlineBtnText}>Cancelar</Text>
            </TouchableOpacity>
          ) : null}
          <TouchableOpacity
            style={[styles.primaryBtnFlex, saving && styles.disabled]}
            onPress={onSave}
            disabled={saving}
            accessibilityRole="button"
          >
            <Text style={styles.primaryBtnText}>
              {saving ? 'Guardando…' : 'Guardar servicios'}
            </Text>
          </TouchableOpacity>
        </View>
      ) : null}

      <MasterFilterSheetModal
        visible={pickerOpen}
        title="Agregar servicio"
        options={availableOptions}
        selectedId={null}
        showAllOption={false}
        appearance="ticket"
        onSelect={addId}
        onClose={() => setPickerOpen(false)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { gap: 10 },
  hint: {
    fontSize: 12,
    color: agenda.ink2,
    lineHeight: 17,
    fontFamily: agendaFonts.sans,
  },
  emptyCatalog: {
    fontSize: 13,
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  emptyAll: {
    borderWidth: 1,
    borderColor: agenda.border,
    borderRadius: agenda.radius,
    backgroundColor: agenda.surface,
    paddingHorizontal: 12,
    paddingVertical: 14,
    gap: 4,
  },
  emptyAllTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
  },
  list: {
    borderWidth: 1,
    borderColor: agenda.border,
    borderRadius: agenda.radius,
    backgroundColor: agenda.surface,
    overflow: 'hidden',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: agenda.surface,
  },
  rowDivider: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: agenda.border,
  },
  rowBody: { flex: 1, minWidth: 0, gap: 2 },
  rowName: {
    fontSize: 14,
    fontWeight: '600',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
  },
  rowMeta: {
    fontSize: 12,
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  removeText: {
    fontSize: 13,
    fontWeight: '600',
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  actionsWide: {
    flexWrap: 'nowrap',
    alignItems: 'center',
  },
  outlineBtn: {
    borderWidth: 1,
    borderColor: agenda.border,
    borderRadius: agenda.radius,
    backgroundColor: agenda.surface,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  outlineBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
  },
  primaryBtnInline: {
    marginLeft: 'auto',
    backgroundColor: agenda.brand,
    borderRadius: agenda.radius,
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignItems: 'center',
    minHeight: 40,
    justifyContent: 'center',
  },
  primaryBtnText: {
    color: agenda.brandInk,
    fontWeight: '700',
    fontSize: 14,
    fontFamily: agendaFonts.sans,
  },
  saveRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
  },
  outlineBtnFlex: {
    flex: 0,
    borderWidth: 1,
    borderColor: agenda.border,
    borderRadius: agenda.radius,
    backgroundColor: agenda.surface,
    paddingHorizontal: 14,
    paddingVertical: 12,
    alignItems: 'center',
    minHeight: 44,
    justifyContent: 'center',
  },
  primaryBtnFlex: {
    flex: 1,
    backgroundColor: agenda.brand,
    borderRadius: agenda.radius,
    paddingVertical: 12,
    paddingHorizontal: 14,
    alignItems: 'center',
    minHeight: 44,
    justifyContent: 'center',
  },
  disabled: { opacity: 0.5 },
});
